import { Building2, LogIn, Mail, UserPlus } from 'lucide-react'
import { useParams } from 'react-router'

import { InviteResult, OrganizationVisibility } from '@ankor-io/common/types/account'
import { Button } from '@ankor-io/ui-components/src/Button'
import { Heading } from '@ankor-io/ui-components/src/Heading'

import { useGetInviteQuery } from '@/api/queries/useUsersQuery'

const InvitationLanding: React.FC = () => {
  const { inviteId } = useParams()

  const {
    data: inviteResult,
    isLoading,
    error,
  } = useGetInviteQuery({
    inviteId: inviteId || '',
    enabled: Boolean(inviteId),
  })

  // Determine invite status from query result
  let inviteStatus: InviteResult | 'error' | 'loading'
  if (isLoading) {
    inviteStatus = 'loading'
  } else if (error) {
    inviteStatus = 'error'
  } else if (inviteResult) {
    inviteStatus = inviteResult.status
  } else {
    inviteStatus = InviteResult.NOT_FOUND
  }

  // Extract invite data from result
  const inviteData = inviteResult?.data || {
    inviteId: inviteId || '',
    invitedByEmail: '',
    inviteeEmail: '',
    organization: {
      description: '',
      id: '',
      name: '',
      visibility: OrganizationVisibility.VISIBLE,
    },
    requireSignup: true,
  }

  const renderContent = () => {
    switch (inviteStatus) {
      case 'loading':
        return (
          <div className="text-center space-y-4">
            <div className="mx-auto size-12 animate-spin rounded-full border-b-2 border-gray-900 dark:border-gray-100"></div>
            <p className="text-gray-600 dark:text-gray-400">Verifying your invitation...</p>
          </div>
        )

      case InviteResult.ACCEPTED:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="flex size-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/20">
                  <Mail className="size-8 text-primary-600 dark:text-primary-400" />
                </div>
              </div>
              <Heading level={1}>You're Invited!</Heading>
              <div className="space-y-2">
                <p className="text-xl text-gray-600 dark:text-gray-400">
                  {inviteData.invitedByEmail} has invited you to join
                </p>
                <div className="flex items-center justify-center gap-2">
                  <Building2 className="size-5 text-gray-500 dark:text-gray-400" />
                  <span className="text-xl font-semibold text-gray-900 dark:text-white">
                    {inviteData.organization.name}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-6 text-center border border-gray-200 dark:border-gray-700">
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                {inviteData.requireSignup
                  ? "You'll need to create an account to accept this invitation."
                  : 'Invitation accepted! You can now sign in to your account.'}
              </p>

              <div className="flex justify-center gap-4">
                {inviteData.requireSignup ? (
                  <Button
                    color="blue"
                    to={`/sign-up?inviteId=${inviteId}${inviteData.inviteeEmail ? `&email=${encodeURIComponent(inviteData.inviteeEmail)}` : ''}`}
                  >
                    <UserPlus data-slot="icon" />
                    Create Account
                  </Button>
                ) : (
                  <Button
                    color="blue"
                    to={`/sign-in${inviteData.inviteeEmail ? `?email=${encodeURIComponent(inviteData.inviteeEmail)}` : ''}`}
                  >
                    <LogIn data-slot="icon" />
                    Sign In
                  </Button>
                )}
              </div>
            </div>

            {inviteData.inviteeEmail && (
              <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                <p>This invitation was sent to: {inviteData.inviteeEmail}</p>
              </div>
            )}
          </div>
        )

      case InviteResult.EXPIRED:
        return (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="flex size-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                <Mail className="size-8 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <Heading level={1}>Invitation Expired</Heading>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              This invitation from {inviteData.invitedByEmail} <br /> to join {inviteData.organization.name} has
              expired. <br />
              Please contact them for a new invitation.
            </p>
          </div>
        )

      case InviteResult.ALREADY_ACCEPTED:
        return (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="flex size-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
                <Mail className="size-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <Heading level={1}>Invitation Already Used</Heading>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              This invitation has already been accepted. You can sign in to access your account.
            </p>
            <Button
              color="blue"
              className="mx-auto flex items-center gap-2"
              to={`/sign-in${inviteData.inviteeEmail ? `?email=${encodeURIComponent(inviteData.inviteeEmail)}` : ''}`}
            >
              <LogIn className="size-4" />
              Sign In
            </Button>
          </div>
        )

      case InviteResult.NOT_FOUND:
      case InviteResult.NOT_PENDING:
      default:
        return (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="flex size-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                <Mail className="size-8 text-gray-600 dark:text-gray-400" />
              </div>
            </div>
            <Heading level={1}>Invalid Invitation</Heading>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              This invitation link is invalid or has been corrupted. Please check the link or contact the person who
              invited you.
            </p>
          </div>
        )
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-linear-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4">
      <div className="w-full max-w-2xl">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 shadow-lg dark:shadow-gray-900/50">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>
            Need help? Contact our support team at{' '}
            <a
              className="text-blue-500"
              href="mailto:support@ankor.io"
            >
              support@ankor.io
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default InvitationLanding
